import React, { useState } from 'react';
import { 
  GraduationCap, 
  BookOpen, 
  Award, 
  Mail, 
  Users, 
  Star, 
  CheckCircle, 
  Clock,
  DollarSign,
  TrendingUp,
  Shield,
  Heart,
  Target,
  Briefcase,
  Phone,
  Send,
  User,
  FileText,
  Calendar,
  Globe,
  MapPin
} from 'lucide-react';

const JoinUs = () => {
  const [activeTab, setActiveTab] = useState('requirements');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    education: '',
    subjects: '',
    availability: '',
    motivation: ''
  });

  const benefits = [
    {
      icon: DollarSign,
      title: "راتب تنافسي",
      description: "أجور عادلة ومجزية تتناسب مع خبرتك ومؤهلاتك",
      color: "bg-green-500"
    },
    {
      icon: Clock,
      title: "مرونة في العمل",
      description: "اختر أوقات العمل التي تناسب ظروفك الشخصية",
      color: "bg-blue-500"
    },
    {
      icon: TrendingUp,
      title: "نمو مهني مستمر",
      description: "فرص تطوير وتدريب مستمرة لتحسين مهاراتك",
      color: "bg-purple-500"
    },
    {
      icon: Shield,
      title: "بيئة عمل آمنة",
      description: "بيئة عمل داعمة ومحترمة لجميع أعضاء الفريق",
      color: "bg-teal-500"
    },
    {
      icon: Heart,
      title: "تأثير إيجابي",
      description: "ساهم في تغيير حياة الطلاب وبناء مستقبلهم",
      color: "bg-red-500"
    },
    {
      icon: Target,
      title: "أهداف واضحة",
      description: "خطط عمل واضحة ومؤشرات أداء محددة",
      color: "bg-amber-500"
    }
  ];

  const applicationSteps = [
    {
      step: 1,
      title: "تقديم الطلب",
      description: "املأ نموذج التقديم أو أرسل سيرتك الذاتية",
      icon: FileText
    },
    {
      step: 2,
      title: "المراجعة الأولية",
      description: "مراجعة الوثائق والمؤهلات",
      icon: User
    },
    {
      step: 3,
      title: "المقابلة الشخصية",
      description: "مقابلة عبر الفيديو مع فريق التوظيف",
      icon: Calendar
    },
    {
      step: 4,
      title: "الاختبار التطبيقي",
      description: "جلسة تدريس تجريبية قصيرة",
      icon: BookOpen
    },
    {
      step: 5,
      title: "القرار النهائي",
      description: "إشعار بالقبول وبدء التدريب",
      icon: CheckCircle
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const message = `
🎓 طلب انضمام جديد - النداء التعليمي

👤 الاسم الكامل: ${formData.fullName}
📧 البريد الإلكتروني: ${formData.email}
📱 رقم الهاتف: ${formData.phone}
💼 الوظيفة المرغوبة: ${formData.position}

📚 المؤهل التعليمي: ${formData.education}
⏰ سنوات الخبرة: ${formData.experience}
📖 المواد التي يمكن تدريسها: ${formData.subjects}
🕐 الأوقات المتاحة: ${formData.availability}

💭 سبب الرغبة في الانضمام:
${formData.motivation}

---
تم إرسال هذا الطلب من موقع النداء التعليمي
    `.trim();

    const whatsappUrl = `https://wa.me/+9660590317360?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-7xl mx-auto">
          
          {/* Hero Section */}
          <div className="text-center mb-20">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-16 rounded-3xl shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full bg-black/10"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-center mb-8">
                  <div className="bg-white/20 p-6 rounded-full mr-4">
                    <Briefcase className="h-12 w-12 text-white" />
                  </div>
                  <h1 className="text-5xl md:text-6xl font-bold">
                    انضم إلى فريق النداء
                  </h1>
                </div>
                <p className="text-2xl opacity-90 max-w-4xl mx-auto leading-relaxed mb-8">
                  كن جزءاً من رحلة تعليمية مميزة وساهم في بناء مستقبل أجيال من الطلاب المتميزين
                </p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <button 
                    onClick={() => setActiveTab('application')}
                    className="px-10 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg text-xl"
                  >
                    قدم طلبك الآن
                  </button>
                  <a 
                    href="mailto:info@elnedaa-education.com?subject=طلب انضمام لفريق النداء التعليمي&body=مرحباً، أرغب في الانضمام إلى فريق النداء التعليمي."
                    className="px-10 py-4 bg-transparent border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-blue-600 transition-all duration-300 text-xl"
                  >
                    أرسل سيرتك الذاتية
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex flex-wrap justify-center mb-16 bg-white rounded-2xl shadow-lg p-2">
            {[
              { id: 'requirements', label: 'المتطلبات', icon: CheckCircle },
              { id: 'benefits', label: 'المزايا', icon: Star },
              { id: 'process', label: 'عملية التقديم', icon: Target },
              { id: 'application', label: 'نموذج التقديم', icon: FileText }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-6 py-3 rounded-xl font-semibold transition-all duration-300 m-1 ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="h-5 w-5 ml-2" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Sections */}
          {activeTab === 'requirements' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div className="bg-white p-10 rounded-2xl shadow-xl">
                <div className="flex items-center mb-8">
                  <div className="bg-blue-100 p-4 rounded-xl mr-4">
                    <GraduationCap className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">المؤهلات الأكاديمية</h3>
                </div>
                <ul className="space-y-4 text-lg">
                  {[
                    "شهادة بكالوريوس في التخصص المطلوب",
                    "معدل تراكمي لا يقل عن 3.0 من 4.0",
                    "شهادات إضافية في التعليم (مفضلة)",
                    "إتقان اللغة العربية والإنجليزية"
                  ].map((req, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-6 w-6 text-green-500 ml-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white p-10 rounded-2xl shadow-xl">
                <div className="flex items-center mb-8">
                  <div className="bg-teal-100 p-4 rounded-xl mr-4">
                    <Users className="h-8 w-8 text-teal-600" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">المهارات المطلوبة</h3>
                </div>
                <ul className="space-y-4 text-lg">
                  {[
                    "خبرة في التدريس عن بُعد (سنة على الأقل)",
                    "مهارات تواصل وتفاعل ممتازة",
                    "إتقان استخدام التقنيات التعليمية",
                    "صبر وقدرة على التحفيز والإلهام"
                  ].map((skill, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-6 w-6 text-green-500 ml-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{skill}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'benefits' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  <div className={`${benefit.color} w-16 h-16 rounded-xl flex items-center justify-center mb-6`}>
                    <benefit.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{benefit.title}</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'process' && (
            <div className="mb-16">
              <div className="text-center mb-12">
                <h3 className="text-4xl font-bold text-gray-900 mb-4">عملية التقديم</h3>
                <p className="text-xl text-gray-600">خطوات بسيطة للانضمام إلى فريقنا</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                {applicationSteps.map((step, index) => (
                  <div key={index} className="text-center">
                    <div className="relative mb-6">
                      <div className="bg-blue-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <step.icon className="h-10 w-10 text-white" />
                      </div>
                      <div className="absolute -top-2 -right-2 bg-amber-500 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {step.step}
                      </div>
                      {index < applicationSteps.length - 1 && (
                        <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gray-300 transform -translate-y-1/2"></div>
                      )}
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h4>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'application' && (
            <div className="max-w-4xl mx-auto mb-16">
              <div className="bg-white p-10 rounded-2xl shadow-xl">
                <div className="text-center mb-10">
                  <div className="flex items-center justify-center mb-6">
                    <FileText className="h-10 w-10 text-blue-600 ml-4" />
                    <h3 className="text-4xl font-bold text-gray-900">نموذج التقديم</h3>
                  </div>
                  <p className="text-xl text-gray-600">املأ البيانات التالية وسيتم إرسال طلبك عبر واتساب</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="fullName" className="block text-gray-700 font-semibold mb-2">
                        الاسم الكامل *
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        placeholder="اسمك الكامل"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                        البريد الإلكتروني *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        placeholder="بريدك الإلكتروني"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                        رقم الهاتف *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        placeholder="رقم هاتفك"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="position" className="block text-gray-700 font-semibold mb-2">
                        الوظيفة المرغوبة *
                      </label>
                      <select
                        id="position"
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        required
                      >
                        <option value="">اختر الوظيفة</option>
                        <option value="معلم رياضيات">معلم رياضيات</option>
                        <option value="معلم علوم">معلم علوم</option>
                        <option value="معلم لغة إنجليزية">معلم لغة إنجليزية</option>
                        <option value="معلم لغة عربية">معلم لغة عربية</option>
                        <option value="معلم مناهج دولية">معلم مناهج دولية</option>
                        <option value="أخرى">أخرى</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="education" className="block text-gray-700 font-semibold mb-2">
                        المؤهل التعليمي *
                      </label>
                      <input
                        type="text"
                        id="education"
                        name="education"
                        value={formData.education}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        placeholder="مثال: بكالوريوس رياضيات"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="experience" className="block text-gray-700 font-semibold mb-2">
                        سنوات الخبرة *
                      </label>
                      <select
                        id="experience"
                        name="experience"
                        value={formData.experience}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        required
                      >
                        <option value="">اختر سنوات الخبرة</option>
                        <option value="أقل من سنة">أقل من سنة</option>
                        <option value="1-2 سنة">1-2 سنة</option>
                        <option value="3-5 سنوات">3-5 سنوات</option>
                        <option value="أكثر من 5 سنوات">أكثر من 5 سنوات</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subjects" className="block text-gray-700 font-semibold mb-2">
                      المواد التي يمكنك تدريسها *
                    </label>
                    <input
                      type="text"
                      id="subjects"
                      name="subjects"
                      value={formData.subjects}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      placeholder="مثال: الرياضيات، الفيزياء، الكيمياء"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="availability" className="block text-gray-700 font-semibold mb-2">
                      الأوقات المتاحة للعمل *
                    </label>
                    <input
                      type="text"
                      id="availability"
                      name="availability"
                      value={formData.availability}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      placeholder="مثال: الأحد إلى الخميس من 4 إلى 9 مساءً"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="motivation" className="block text-gray-700 font-semibold mb-2">
                      لماذا تريد الانضمام إلى فريق النداء؟ *
                    </label>
                    <textarea
                      id="motivation"
                      name="motivation"
                      value={formData.motivation}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      placeholder="اكتب دوافعك للانضمام إلى فريقنا..."
                      required
                    />
                  </div>

                  <div className="text-center pt-6">
                    <button
                      type="submit"
                      className="px-12 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg text-xl"
                    >
                      إرسال الطلب عبر واتساب
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Application Section */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-12 rounded-3xl shadow-2xl text-center">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-center mb-8">
                <Send className="h-12 w-12 ml-4" />
                <h3 className="text-4xl font-bold">ابدأ رحلتك معنا اليوم</h3>
              </div>
              
              <p className="text-xl opacity-90 mb-10 leading-relaxed">
                انضم إلى فريق من المعلمين المتميزين واجعل تأثيراً إيجابياً في حياة الطلاب. نحن نبحث عن أشخاص متحمسين ومبدعين لينضموا إلى مهمتنا التعليمية.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                  <Mail className="h-8 w-8 mx-auto mb-4" />
                  <h4 className="text-xl font-bold mb-2">أرسل سيرتك الذاتية</h4>
                  <a 
                    href="mailto:info@elnedaa-education.com?subject=طلب انضمام لفريق النداء التعليمي&body=مرحباً، أرغب في الانضمام إلى فريق النداء التعليمي. أرفق سيرتي الذاتية مع هذا البريد."
                    className="opacity-90 hover:underline"
                  >
                    info@elnedaa-education.com
                  </a>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                  <Phone className="h-8 w-8 mx-auto mb-4" />
                  <h4 className="text-xl font-bold mb-2">تواصل مباشر</h4>
                  <p className="opacity-90">0590317360</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                  <Globe className="h-8 w-8 mx-auto mb-4" />
                  <h4 className="text-xl font-bold mb-2">العمل عن بُعد</h4>
                  <p className="opacity-90">من أي مكان في المملكة</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a 
                  href="mailto:info@elnedaa-education.com?subject=طلب انضمام لفريق النداء التعليمي&body=مرحباً، أرغب في الانضمام إلى فريق النداء التعليمي. أرفق سيرتي الذاتية مع هذا البريد."
                  className="inline-block bg-white text-teal-700 font-bold py-4 px-10 rounded-xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg text-xl"
                >
                  أرسل سيرتك الذاتية
                </a>
                <a 
                  href="https://wa.me/+9660590317360?text=مرحباً، أرغب في الاستفسار عن فرص العمل المتاحة في النداء التعليمي"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-transparent border-2 border-white text-white font-bold py-4 px-10 rounded-xl hover:bg-white hover:text-teal-700 transition-all duration-300 text-xl"
                >
                  تواصل عبر واتساب
                </a>
              </div>
            </div>
          </div>

          {/* Statistics Section */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
              <div className="text-4xl font-bold text-blue-600 mb-2">50+</div>
              <div className="text-gray-600 text-lg">معلم متميز</div>
            </div>
            <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
              <div className="text-4xl font-bold text-teal-600 mb-2">+1000</div>
              <div className="text-gray-600 text-lg">طالب راضٍ</div>
            </div>
            <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
              <div className="text-4xl font-bold text-amber-500 mb-2">95%</div>
              <div className="text-gray-600 text-lg">معدل الرضا</div>
            </div>
            <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
              <div className="text-4xl font-bold text-purple-600 mb-2">24/7</div>
              <div className="text-gray-600 text-lg">دعم مستمر</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinUs;